import { Button } from '@hologram-dimension/button';
import { Link } from '@hologram-dimension/link';
import { Modal } from '@hologram-dimension/modal';
import clsx from 'clsx/lite';
import SetUsageAlertsForm from 'components/SetUsageAlerts/SetUsageAlerts';
import type { CommonModalProps } from 'modals/shared';
import { useCallback, useRef, useState, type FC } from 'react';
import type { AlertToggle, HologramSimIdsMap, OnSubmit } from 'types/common';
import { SetUsageAlertsArticle } from 'utils/constants/links';
import formatNumber from 'utils/numbers/formatNumber';

interface SetUsageAlertsProps extends CommonModalProps {
  sims: HologramSimIdsMap;
  onSuccess: (sims: Array<HologramSimId>) => void;
}

// TODO: A lot of the modals share similar pumbing and handling. I wonder if we could extract one shared component that handles the common logic.

const SetUsageAlerts: FC<SetUsageAlertsProps> = ({
  onClose,
  defaultOpen = false,
  className,
  sims,
  onSuccess,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [processing, setIsProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const submitRef = useRef<OnSubmit>();

  const onProcessing = useCallback((value: boolean) => {
    setIsProcessing(value);
  }, []);

  const onDone = useCallback(
    (modifiedSims: Array<HologramSimId>) => {
      setDone(true);
      if (modifiedSims) {
        onSuccess(modifiedSims);
      }
      onClose();
    },
    [onClose, onSuccess]
  );

  const handleSubmit: React.MouseEventHandler &
    React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };

  const [alerting, setAlerting] = useState<AlertToggle>('noalert');

  return (
    <Modal
      className={clsx(className)}
      header={`Set data usage alert${sims.size === 1 ? '' : ` for ${formatNumber(sims.size)} SIMs`}`}
      onClosed={onClose}
      open={open}
      setOpen={setOpen}
      noContentPadding
      contentWell={
        <>
          Monitor when your {sims.size === 1 ? 'SIM reaches' : 'SIMs reach'} a set threshold or
          amount of data usage. Data usage alerts are most often used as an early warning before
          reaching a data limit.{' '}
          <Link href={SetUsageAlertsArticle} target="_blank" rel="noreferrer" type="inline">
            Learn more
          </Link>
        </>
      }
      footerActions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && (
            <Button
              loading={processing}
              onClick={handleSubmit}
              variant={alerting === 'noalert' ? 'delete' : 'primary'}
            >
              {alerting === 'noalert' ? 'Remove usage threshold' : 'Update usage threshold'}
            </Button>
          )}
        </>
      }
    >
      <SetUsageAlertsForm
        sims={sims}
        onProcessing={onProcessing}
        ref={submitRef}
        onDone={onDone}
        onAlertingToggle={setAlerting}
      />
    </Modal>
  );
};

export default SetUsageAlerts;
